'use client';

import { useEffect } from 'react';
import sha1 from 'crypto-js/sha1';
import Hex from 'crypto-js/enc-hex';
import { removeCookieBanner } from '../components/organisms/Navigation/utils/oneTrust';

export const UTMAttribution = () => {
  useEffect(() => {
    try {
      const name = 'attribution';
      const windowExists = typeof window !== 'undefined';
      const localData = JSON.parse(windowExists ? window?.sessionStorage?.getItem(name) : null);
      const currentTime = new Date().getTime();
      const timeframe = 1 * 24 * 60 * 60 * 1000; // 1 day
      const expiration = currentTime - timeframe; // 1 day in the past
      const urlParams = windowExists ? window?.location?.search : '';
      let params;
      let referrerUrl;
      let medium;
      let clientid = 'n/a';
      let retryCounter = 0;

      // W20-7800
      const getCookie = (name) => document?.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))?.at(2);
      const setClientID = () => {
        const GoogleAnalyticsCookie = getCookie('_ga');
        let regexp = /^([^.]+)\.([^.]+)\.(.+)$/;
        if (GoogleAnalyticsCookie) clientid = GoogleAnalyticsCookie.replace(regexp, '$3');
      };

      // W20-7799
      const sources = [
        { domain: 'google', medium: 'organic' },
        { domain: 'bing', medium: 'organic' },
        { domain: 'yahoo', medium: 'organic' },
        { domain: 'duckduckgo', medium: 'organic' },
        { domain: 'gbm-listing', medium: 'organic' },
        { domain: 'msn', medium: 'organic' },
        { domain: 'ecosia', medium: 'organic' },
        { domain: 'baidu', medium: 'organic' },
        { domain: 'naver', medium: 'organic' },
        { domain: 'daum', medium: 'organic' },
        { domain: 'yandex', medium: 'organic' },
        { domain: 'aol', medium: 'organic' },
        { domain: 'qwant', medium: 'organic' },
        { domain: 'facebook', medium: 'social' },
        { domain: 't.co', medium: 'social' },
        { domain: 'linkedin', medium: 'social' },
        { domain: 'twitter', medium: 'social' },
        { domain: 'youtube', medium: 'social' },
        { domain: 'instagram', medium: 'social' },
        { domain: 'reddit', medium: 'social' },
        { domain: 'stocktwits', medium: 'social' },
        { domain: 'tap.bio', medium: 'social' },
      ];

      const setReferrerAndMedium = () => {
        referrerUrl = document.referrer;
        if (referrerUrl) {
          let match = referrerUrl.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?=]+)/im);
          let urlMedium = match[1];
          referrerUrl = urlMedium; // domain.com only
          const searchArray = sources.filter((source) => urlMedium.includes(source.domain));
          if (searchArray.length) {
            // user was linked to us from a known source
            medium = searchArray[0].medium;
          } else {
            // user was linked to us from an unknown source
            medium = 'referral';
          }
        } else {
          // user most likely manually typed our url
          referrerUrl = 'n/a';
          medium = 'direct';
        }
      };

      if (windowExists && urlParams && !localData) {
        const objFromEntries = (entries) =>
          entries.reduce((prev, curr) => {
            prev[curr[0]] = curr[1];
            return prev;
          }, {});
        let entries = urlParams
          .replace('?', '')
          .split('&')
          .map((a) => a.split('='))
          .map((b) => b.map((c) => decodeURI(c)));
        params = objFromEntries(entries);

        // W20-18972: default UTM values in payload when gclid is present but utm_source and utm_medium are missing
        if (params['gclid']) {
          params['utm_source'] ||= 'google';
          params['utm_medium'] ||= 'cpc';
        }
      } else {
        params = {};
      }

      const setSessionStorage = () => {
        const data = JSON.stringify({
          source: params['utm_source'] || referrerUrl,
          medium: params['utm_medium'] || medium,
          campaign: params['utm_campaign'] || 'n/a',
          content: params['utm_content'] || 'n/a',
          term: params['utm_term'] || 'n/a',
          clientid: clientid,
          timestamp: currentTime,
        });
        window?.sessionStorage?.setItem(name, data);
      };

      const remove = () => window?.sessionStorage?.removeItem(name);

      const checkIfAnalyticsLoaded = () => {
        // Check every 500ms if Google Analytics is loaded before setting the cookie.
        // If retry fails 13 times (6.5 seconds max per Kyle), give up and set the cookie using the default clientid.
        // eslint-disable-next-line no-undef
        if ((typeof ga === 'function' && ga.loaded) || retryCounter++ > 12) {
          // if expired cookie exists, remove it
          if (localData && localData.timestamp < expiration) {
            remove();
          }
          setClientID();
          setReferrerAndMedium();
          setSessionStorage();
        } else {
          setTimeout(() => checkIfAnalyticsLoaded(), 500);
        }
      };

      // if new cookie exists, do nothing
      if (!localData || (localData && localData.timestamp < expiration)) {
        checkIfAnalyticsLoaded();
      }

      // Close OneTrust if we are on https://stage.dslmotors.com only (all other domains are not affected)
      if (window?.origin === 'https://stage.dslmotors.com') {
        removeCookieBanner();
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  return <></>;
};

export const UTMAttributionHistory = (email, attributionData) => {
  const windowExists = typeof window !== 'undefined';
  const attributionUser = JSON.parse(windowExists ? localStorage.getItem('attributionUser') : null);

  try {
    const emailSha1 = sha1(email);
    const emailHash = emailSha1.toString(Hex);

    if (attributionUser) {
      const attributionArray = [...attributionUser.attributionArchive];
      // If current email hash matches cookie hash
      if (attributionUser.UserID === emailHash) {
        const lastCookieEntry = attributionArray.length - 1;
        const lastCookieTimestamp = attributionArray[lastCookieEntry]?.timestamp;
        const currentTimestamp = attributionData?.timestamp;

        // If current UTMAttribution data is newer than last saved in the history
        if (currentTimestamp > lastCookieTimestamp) {
          // Push the new entry to the array and re-save the cookie
          attributionArray.push(attributionData);
          const attributionUserData = JSON.stringify({
            UserID: emailHash,
            attributionArchive: attributionArray,
          });
          localStorage.setItem('attributionUser', attributionUserData);
          return attributionUserData;
        } else {
          const attributionUserNoChange = JSON.stringify({
            UserID: emailHash,
            attributionArchive: attributionArray,
          });
          return attributionUserNoChange;
        }
      } else {
        const attributionUserDifferent = JSON.stringify({
          UserID: emailHash, // hash did not match prior hash, so overwrite
          attributionArchive: attributionArray,
        });
        return attributionUserDifferent;
      }
    } else {
      // If "attributionUser" localStorage cookie does not exist, create it
      const attributionUserData = JSON.stringify({
        UserID: emailHash,
        attributionArchive: [attributionData],
      });
      localStorage.setItem('attributionUser', attributionUserData);
      return attributionUserData;
    }
  } catch (error) {
    console.error(error);
    return error;
  }
};
